import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Download, Eye, FileArchive, FileText, Files, FolderOpen, Info, Loader2, Pause, Play, Undo2, Upload, X,
} from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/components/language-provider";
import { queryClient } from "@/lib/queryClient";
import { invalidateDocumentQueries } from "@/lib/live-documents";
import {
  ImportError, ImportRunner, MANIFEST_TEMPLATE, importApi, pickedFromInput, readDrop, scanFiles, scanZip,
  type ImportConfig, type ImportRecord, type PickedFile, type RunError, type RunOutcome, type RunProgress, type ScanResult,
} from "@/lib/import-client";

type Phase = "pick" | "scanning" | "review" | "running" | "done";

const fill = (s: string, vars: Record<string, string | number>) => s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ""));

/** An open import nobody has touched for a while was left behind (tab closed, session expired). */
const INTERRUPTED_AFTER_MS = 10 * 60_000;

export default function DocumentImport() {
  const [, setLocation] = useLocation();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const locale = language === "pt" ? "pt-BR" : "en-US";
  const nf = (n: number) => n.toLocaleString(locale);
  const size = (n: number) => {
    const units = ["KB", "MB", "GB"];
    let v = n / 1024;
    let u = 0;
    while (v >= 1024 && u < units.length - 1) { v /= 1024; u++; }
    return `${v.toLocaleString(locale, { maximumFractionDigits: 1 })} ${units[u]}`;
  };
  const duration = (sec: number) => {
    if (sec < 60) return `${Math.max(1, sec)} s`;
    const m = Math.round(sec / 60);
    return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`;
  };
  const codeLabel = (code: string) => { const k = `import.code.${code}`; const v = t(k); return v === k ? t("import.code.failed") : v; };

  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    if (!localStorage.getItem("token")) { setLocation("/login"); return; }
    const stored = localStorage.getItem("user");
    if (stored) setUser(JSON.parse(stored));
  }, [setLocation]);

  const { data: cfg, isLoading: cfgLoading, error: cfgError } = useQuery<ImportConfig>({
    queryKey: ["/api/documents/imports", "config"],
    queryFn: importApi.config,
    enabled: !!user,
  });
  const { data: history, refetch: refetchHistory } = useQuery<ImportRecord[]>({
    queryKey: ["/api/documents/imports", "list"],
    queryFn: importApi.list,
    enabled: !!user,
  });

  const [phase, setPhase] = useState<Phase>("pick");
  const [scanName, setScanName] = useState("");
  const [scan, setScan] = useState<ScanResult | null>(null);
  const [name, setName] = useState("");
  const [sourceSystem, setSourceSystem] = useState("");
  const [progress, setProgress] = useState<RunProgress | null>(null);
  const [errors, setErrors] = useState<RunError[]>([]);
  const [outcome, setOutcome] = useState<RunOutcome | null>(null);
  const [current, setCurrent] = useState<ImportRecord | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [undoTarget, setUndoTarget] = useState<ImportRecord | null>(null);
  const [undoing, setUndoing] = useState<{ id: string; remaining: number } | null>(null);
  const runner = useRef<ImportRunner | null>(null);
  const zipInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const filesInput = useRef<HTMLInputElement>(null);

  // Closing the tab mid-import stops it; the browser asks first.
  useEffect(() => {
    if (phase !== "running") return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = t("import.leaveWarning"); return e.returnValue; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [phase, t]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setLocation("/");
  };

  const startScan = async (label: string, work: () => Promise<ScanResult>) => {
    setScanName(label);
    setPhase("scanning");
    try {
      const result = await work();
      setScan(result);
      setName(result.sourceName);
      setPhase("review");
    } catch (e) {
      setPhase("pick");
      const bad = e instanceof ImportError && e.code === "bad_zip";
      toast({ title: t("common.error"), description: bad ? t("import.badZip") : e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
  };

  const onZip = (file: File | undefined) => { if (file && cfg) void startScan(file.name, () => scanZip(file, cfg)); };
  const onFiles = (files: PickedFile[], label: string) => { if (files.length && cfg) void startScan(label, () => scanFiles(files, cfg, label)); };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (!cfg || phase !== "pick") return;
    const dropped = readDrop(e.dataTransfer);
    void dropped.then(({ zip, files }) => {
      if (zip) onZip(zip);
      else {
        const top = files[0]?.path.split("/")[0];
        const oneFolder = !!top && files.every((f) => f.path.startsWith(top + "/"));
        onFiles(files, oneFolder ? top : t("import.pickFiles"));
      }
    });
  };

  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob([MANIFEST_TEMPLATE], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "manifesto.csv";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const resumable = useMemo(
    () => (scan ? history?.find((h) => h.status === "open" && h.name === name.trim()) ?? null : null),
    [history, name, scan],
  );

  const start = async () => {
    if (!scan || !cfg) return;
    let record: ImportRecord;
    try {
      record = resumable ?? await importApi.create({
        name: name.trim() || scan.sourceName,
        sourceSystem: sourceSystem.trim() || undefined,
        expectedCount: scan.items.length,
        expectedBytes: scan.totalBytes,
      });
    } catch (e) {
      toast({ title: t("common.error"), description: e instanceof Error ? e.message : String(e), variant: "destructive" });
      return;
    }
    setCurrent(record);
    setErrors([]);
    setOutcome(null);
    setPhase("running");
    const r = new ImportRunner({
      importId: record.id,
      items: scan.items,
      skipped: scan.skipped,
      // A continued import already has the first run's rejections in its report.
      reportSkipped: !resumable,
      cfg,
      onUpdate: (p, errs) => { setProgress(p); setErrors(errs.slice(-200)); },
    });
    runner.current = r;
    const result = await r.run();
    runner.current = null;
    setOutcome(result);
    setPhase("done");
    void refetchHistory();
    void invalidateDocumentQueries(queryClient);
  };

  const reset = () => {
    setScan(null);
    setProgress(null);
    setErrors([]);
    setOutcome(null);
    setCurrent(null);
    setSourceSystem("");
    setPhase("pick");
  };

  const undo = async (record: ImportRecord) => {
    setUndoTarget(null);
    setUndoing({ id: record.id, remaining: record.importedCount });
    let deleted = 0;
    let kept = 0;
    try {
      for (;;) {
        const r = await importApi.undo(record.id);
        deleted += r.deleted;
        kept = r.kept;
        setUndoing({ id: record.id, remaining: r.remaining });
        if (r.remaining === 0 || r.deleted === 0) break;
      }
      toast({ title: fill(t("import.undone"), { n: nf(deleted) }), description: kept ? fill(t("import.undoKept"), { n: nf(kept) }) : undefined });
    } catch (e) {
      toast({ title: t("common.error"), description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setUndoing(null);
      void refetchHistory();
      void invalidateDocumentQueries(queryClient);
    }
  };

  const downloadErrors = (record: ImportRecord) =>
    importApi.downloadErrors(record.id, `importacao-${record.name.replace(/[\\/:*?"<>|]+/g, "_")}-erros.csv`)
      .catch((e) => toast({ title: t("common.error"), description: e instanceof Error ? e.message : String(e), variant: "destructive" }));

  if (!user) return null;

  const skippedByCode = (scan?.skipped ?? []).reduce<Record<string, number>>((acc, s) => { acc[s.code] = (acc[s.code] ?? 0) + 1; return acc; }, {});
  const tooMany = !!scan && !!cfg && scan.items.length > cfg.maxDocumentsPerImport;
  const percent = progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  const statusOf = (h: ImportRecord) => {
    if (h.status !== "open") return h.status;
    if (current?.id === h.id && phase === "running") return "open";
    return Date.now() - new Date(h.updatedAt).getTime() > INTERRUPTED_AFTER_MS ? "interrupted" : "open";
  };
  const STATUS_STYLE: Record<string, string> = {
    open: "bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900",
    interrupted: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900",
    completed: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900",
    cancelled: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-900/60 dark:text-slate-300 dark:border-slate-800",
    undone: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-900/60 dark:text-slate-400 dark:border-slate-800",
  };

  const counters = (imported: number, duplicates: number, failed: number) => (
    <div className="grid grid-cols-3 gap-2 sm:gap-3">
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900 dark:bg-emerald-950/30" data-testid="count-imported">
        <p className="text-xs text-emerald-800 dark:text-emerald-300">{t("import.imported")}</p>
        <p className="text-xl font-semibold text-emerald-900 dark:text-emerald-200">{nf(imported)}</p>
      </div>
      <div className="rounded-lg border bg-muted/40 p-3" data-testid="count-duplicates">
        <p className="text-xs text-muted-foreground">{t("import.duplicates")}</p>
        <p className="text-xl font-semibold">{nf(duplicates)}</p>
      </div>
      <div className={`rounded-lg border p-3 ${failed ? "border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/30" : "bg-muted/40"}`} data-testid="count-failed">
        <p className={`text-xs ${failed ? "text-rose-800 dark:text-rose-300" : "text-muted-foreground"}`}>{t("import.failed")}</p>
        <p className={`text-xl font-semibold ${failed ? "text-rose-900 dark:text-rose-200" : ""}`}>{nf(failed)}</p>
      </div>
    </div>
  );

  const errorList = errors.length > 0 && (
    <details className="rounded-lg border" data-testid="import-errors">
      <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium">{fill(t("import.errors"), { n: nf(progress?.failed ?? errors.length) })}</summary>
      <ul className="max-h-64 divide-y overflow-y-auto text-sm">
        {errors.slice().reverse().map((e, i) => (
          <li key={i} className="flex flex-col gap-0.5 px-3 py-2 sm:flex-row sm:items-center sm:gap-3">
            <span className="min-w-0 flex-1 truncate font-mono text-xs" title={e.path}>{e.path}</span>
            <span className="shrink-0 text-xs text-rose-700 dark:text-rose-300">{codeLabel(e.code)}{e.detail ? ` · ${e.detail}` : ""}</span>
          </li>
        ))}
      </ul>
    </details>
  );

  return (
    <AppLayout user={user} onLogout={handleLogout} currentPage="/documents">
      <div className="mb-6 flex items-start gap-2 sm:gap-3">
        <Link href="/documents">
          <Button variant="ghost" size="icon" aria-label={t("import.back")} data-testid="button-back"><ArrowLeft className="h-5 w-5" /></Button>
        </Link>
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold">{t("import.title")}</h1>
          <p className="text-muted-foreground">{t("import.subtitle")}</p>
        </div>
      </div>

      {cfgLoading && <Card><CardContent className="flex items-center gap-3 py-10 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> {t("common.loading")}</CardContent></Card>}

      {cfgError && (
        <Card><CardContent className="py-8 text-sm text-destructive">{cfgError instanceof Error ? cfgError.message : String(cfgError)}</CardContent></Card>
      )}

      {cfg && !cfg.allowed && (
        <Card data-testid="import-not-allowed">
          <CardContent className="flex flex-col gap-4 py-8 sm:flex-row sm:items-center">
            <Info className="h-8 w-8 shrink-0 text-primary" />
            <p className="flex-1">{(() => { const k = `import.notAllowed.${cfg.reason}`; const v = t(k); return v === k ? t("import.notAllowed.other") : v; })()}</p>
            {(cfg.reason === "free_plan" || cfg.reason === "subscription_inactive") && (
              <Link href="/settings?tab=subscription"><Button>{t("import.seePlans")}</Button></Link>
            )}
          </CardContent>
        </Card>
      )}

      {cfg?.allowed && (phase === "pick" || phase === "scanning") && (
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">{t("import.step1")}</CardTitle>
              <CardDescription>{fill(t("import.limits"), { mb: Math.round(cfg.maxFileBytes / 1024 / 1024), max: nf(cfg.maxDocumentsPerImport) })}</CardDescription>
            </CardHeader>
            <CardContent>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`flex flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed px-4 py-10 text-center transition-colors ${dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25"}`}
                data-testid="import-dropzone"
              >
                {phase === "scanning" ? (
                  <>
                    <Loader2 className="h-10 w-10 animate-spin text-primary" />
                    <p className="font-medium">{fill(t("import.scanning"), { name: scanName })}</p>
                  </>
                ) : (
                  <>
                    <Upload className="h-10 w-10 text-primary" />
                    <p className="font-medium">{t("import.drop")}</p>
                    <p className="text-sm text-muted-foreground">{t("import.or")}</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      <Button onClick={() => zipInput.current?.click()} data-testid="button-pick-zip"><FileArchive className="mr-2 h-4 w-4" />{t("import.pickZip")}</Button>
                      <Button variant="outline" onClick={() => folderInput.current?.click()} data-testid="button-pick-folder"><FolderOpen className="mr-2 h-4 w-4" />{t("import.pickFolder")}</Button>
                      <Button variant="outline" onClick={() => filesInput.current?.click()} data-testid="button-pick-files"><Files className="mr-2 h-4 w-4" />{t("import.pickFiles")}</Button>
                    </div>
                  </>
                )}
                <input ref={zipInput} type="file" accept=".zip,application/zip" className="hidden" data-testid="input-zip"
                  onChange={(e) => { onZip(e.target.files?.[0]); e.target.value = ""; }} />
                <input ref={folderInput} type="file" className="hidden" data-testid="input-folder"
                  {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
                  onChange={(e) => { const files = pickedFromInput(e.target.files); onFiles(files, files[0]?.path.split("/")[0] || t("import.pickFolder")); e.target.value = ""; }} />
                <input ref={filesInput} type="file" multiple accept=".pdf,.doc,.docx,.csv" className="hidden" data-testid="input-files"
                  onChange={(e) => { onFiles(pickedFromInput(e.target.files), t("import.pickFiles")); e.target.value = ""; }} />
              </div>
              <ul className="mt-5 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                {["how1", "how2", "how3", "how4"].map((k) => (
                  <li key={k} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{t(`import.${k}`)}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg"><FileText className="h-5 w-5" />{t("import.manifestTitle")}</CardTitle>
              <CardDescription>{t("import.manifestDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <pre className="overflow-x-auto rounded-md bg-muted p-3 text-[11px] leading-relaxed">arquivo;titulo;data;status;{"\n"}signatarios;emails;assinado_em;{"\n"}id_externo</pre>
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="button-manifest-template"><Download className="mr-2 h-4 w-4" />{t("import.manifestTemplate")}</Button>
            </CardContent>
          </Card>
        </div>
      )}

      {cfg?.allowed && phase === "review" && scan && (
        <Card data-testid="import-review">
          <CardHeader>
            <CardTitle className="text-lg">{t("import.step2")}</CardTitle>
            <CardDescription className="break-all">{scanName}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">{t("import.ready")}</p>
                <p className="text-2xl font-semibold" data-testid="scan-ready">{nf(scan.items.length)}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">{t("import.totalSize")}</p>
                <p className="text-2xl font-semibold">{size(scan.totalBytes)}</p>
              </div>
              <div className={`rounded-lg border p-3 ${scan.skipped.length ? "border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30" : ""}`}>
                <p className="text-xs text-muted-foreground">{t("import.skipped")}</p>
                <p className="text-2xl font-semibold" data-testid="scan-skipped">{nf(scan.skipped.length)}</p>
                {scan.skipped.length > 0 && (
                  <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
                    {Object.entries(skippedByCode).map(([code, n]) => `${codeLabel(code)}: ${nf(n)}`).join(" · ")}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-1 rounded-lg bg-muted/50 p-3 text-sm" data-testid="scan-manifest">
              {!scan.manifest && <p>{t("import.manifestNone")}</p>}
              {scan.manifest?.error && <p className="text-amber-800 dark:text-amber-300">{fill(t(`import.manifestError.${scan.manifest.error}`), { name: scan.manifest.name })}</p>}
              {scan.manifest && !scan.manifest.error && (
                <>
                  <p className="flex items-center gap-2"><FileText className="h-4 w-4 shrink-0" />{fill(t("import.manifestFound"), { name: scan.manifest.name, rows: nf(scan.manifest.rows), matched: nf(scan.manifest.matched) })}</p>
                  {scan.manifest.unmatched.length > 0 && (
                    <p className="text-xs text-muted-foreground">{t("import.manifestUnmatched")} {scan.manifest.unmatched.slice(0, 5).join(", ")}{scan.manifest.rows - scan.manifest.matched > 5 ? "…" : ""}</p>
                  )}
                </>
              )}
              {scan.ignored > 0 && <p className="text-xs text-muted-foreground">{fill(t("import.ignoredSystem"), { n: nf(scan.ignored) })}</p>}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="import-name">{t("import.name")}</Label>
                <Input id="import-name" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} data-testid="input-import-name" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="import-source">{t("import.source")}</Label>
                <Input id="import-source" value={sourceSystem} maxLength={120} placeholder={t("import.sourcePlaceholder")} onChange={(e) => setSourceSystem(e.target.value)} data-testid="input-import-source" />
              </div>
            </div>

            {resumable && <p className="flex items-start gap-2 text-sm text-sky-800 dark:text-sky-300" data-testid="import-continue-note"><Info className="mt-0.5 h-4 w-4 shrink-0" />{fill(t("import.continueNote"), { name: resumable.name })}</p>}
            {tooMany && <p className="flex items-start gap-2 text-sm text-destructive"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{fill(t("import.tooMany"), { n: nf(scan.items.length), max: nf(cfg.maxDocumentsPerImport) })}</p>}
            {scan.items.length === 0 && <p className="flex items-start gap-2 text-sm text-destructive"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{t("import.nothing")}</p>}

            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void start()} disabled={tooMany || scan.items.length === 0} data-testid="button-start-import">
                <Upload className="mr-2 h-4 w-4" />{fill(t("import.start"), { n: nf(scan.items.length) })}
              </Button>
              <Button variant="ghost" onClick={reset} data-testid="button-choose-other">{t("import.chooseOther")}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {phase === "running" && progress && (
        <Card data-testid="import-running">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              {progress.paused ? <Pause className="h-5 w-5 text-amber-600" /> : <Loader2 className="h-5 w-5 animate-spin text-primary" />}
              {t("import.step3")}{progress.paused ? ` · ${t("import.paused")}` : ""}
            </CardTitle>
            <CardDescription className="break-all">{current?.name}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-2xl font-semibold" data-testid="import-percent">{percent}%</p>
                <p className="text-sm text-muted-foreground">{fill(t("import.progress"), { done: nf(progress.done), total: nf(progress.total) })}</p>
              </div>
              <div className="h-3 w-full overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
                <div className="h-full rounded-full bg-emerald-600 transition-[width] duration-500" style={{ width: `${percent}%` }} />
              </div>
              <p className="text-xs text-muted-foreground">
                {progress.etaSeconds != null && !progress.paused ? fill(t("import.eta"), { time: duration(progress.etaSeconds) }) : " "}
                {progress.rate > 0 && !progress.paused ? ` · ${fill(t("import.rate"), { n: progress.rate.toLocaleString(locale, { maximumFractionDigits: 1 }) })}` : ""}
              </p>
            </div>
            {counters(progress.imported, progress.duplicates, progress.failed)}
            <div className="flex flex-wrap gap-2">
              {progress.paused
                ? <Button onClick={() => runner.current?.resume()} data-testid="button-resume"><Play className="mr-2 h-4 w-4" />{t("import.resume")}</Button>
                : <Button variant="outline" onClick={() => runner.current?.pause()} data-testid="button-pause"><Pause className="mr-2 h-4 w-4" />{t("import.pause")}</Button>}
              <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={() => runner.current?.cancel()} data-testid="button-cancel-import">
                <X className="mr-2 h-4 w-4" />{t("import.cancel")}
              </Button>
            </div>
            <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{t("import.keepOpen")}
            </p>
            {errorList}
          </CardContent>
        </Card>
      )}

      {phase === "done" && progress && outcome && current && (
        <Card data-testid="import-done">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              {outcome.kind === "completed" ? <CheckCircle2 className="h-6 w-6 text-emerald-600" /> : <AlertTriangle className="h-6 w-6 text-amber-600" />}
              {outcome.kind === "completed" ? t("import.doneTitle") : outcome.kind === "cancelled" ? t("import.cancelledTitle") : t("import.stoppedTitle")}
            </CardTitle>
            <CardDescription className="break-all">{current.name}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {outcome.kind === "stopped" && (
              <p className="text-sm text-amber-800 dark:text-amber-300">{outcome.reason === "not_allowed" ? outcome.message : t(`import.stopped.${outcome.reason}`)}</p>
            )}
            {counters(progress.imported, progress.duplicates, progress.failed)}
            <div className="flex flex-wrap gap-2">
              <Link href={`/documents?import=${current.id}`}>
                <Button data-testid="button-view-imported"><Eye className="mr-2 h-4 w-4" />{t("import.viewImported")}</Button>
              </Link>
              {progress.failed > 0 && (
                <Button variant="outline" onClick={() => void downloadErrors(current)} data-testid="button-download-errors"><Download className="mr-2 h-4 w-4" />{t("import.downloadErrors")}</Button>
              )}
              <Button variant="ghost" onClick={reset} data-testid="button-import-more">{t("import.importMore")}</Button>
            </div>
            {errorList}
          </CardContent>
        </Card>
      )}

      {history && history.length > 0 && (
        <Card className="mt-6" data-testid="import-history">
          <CardHeader>
            <CardTitle className="text-lg">{t("import.history")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {history.map((h) => {
              const st = statusOf(h);
              const busy = undoing?.id === h.id;
              return (
                <div key={h.id} className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center" data-testid={`import-row-${h.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-medium">{h.name}</span>
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[st]}`}>{t(`import.status.${st}`)}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {new Date(h.createdAt).toLocaleString(locale, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      {h.sourceSystem ? ` · ${h.sourceSystem}` : ""}
                      {` · ${nf(h.importedCount)} ${t("import.imported").toLowerCase()}`}
                      {h.failedCount ? ` · ${nf(h.failedCount)} ${t("import.failed").toLowerCase()}` : ""}
                    </p>
                    {busy && <p className="mt-1 flex items-center gap-1.5 text-xs text-primary"><Loader2 className="h-3 w-3 animate-spin" />{fill(t("import.undoing"), { n: nf(undoing.remaining) })}</p>}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {h.status !== "undone" && h.importedCount > 0 && (
                      <Link href={`/documents?import=${h.id}`}><Button variant="ghost" size="sm"><Eye className="mr-1.5 h-4 w-4" />{t("import.view")}</Button></Link>
                    )}
                    {h.failedCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={() => void downloadErrors(h)}><Download className="mr-1.5 h-4 w-4" />{t("import.errorsShort")}</Button>
                    )}
                    {h.status !== "undone" && h.importedCount > 0 && !(current?.id === h.id && phase === "running") && (
                      <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" disabled={!!undoing} onClick={() => setUndoTarget(h)} data-testid={`button-undo-${h.id}`}>
                        <Undo2 className="mr-1.5 h-4 w-4" />{t("import.undo")}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <AlertDialog open={!!undoTarget} onOpenChange={(open) => !open && setUndoTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{undoTarget && fill(t("import.undoTitle"), { name: undoTarget.name })}</AlertDialogTitle>
            <AlertDialogDescription>{undoTarget && fill(t("import.undoDesc"), { n: nf(undoTarget.importedCount) })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => undoTarget && void undo(undoTarget)} data-testid="button-confirm-undo">
              {t("import.undoConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
